import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StatusBadge from './StatusBadge';
import './History.css';

function History() {
  const [transactions, setTransactions] = useState([]);
  const [totals,       setTotals]       = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [expandedIds,  setExpandedIds]  = useState(new Set());
  const [linesCache,   setLinesCache]   = useState({});

  useEffect(() => {
    axios.get('/api/transactions')
      .then(res => {
        setTransactions(res.data.transactions);
        setTotals(res.data.totals);
      })
      .catch(() => setError('Failed to load transaction history.'))
      .finally(() => setLoading(false));
  }, []);

  const fmt         = val => `$${Number(val || 0).toFixed(2)}`;
  const fmtDate     = dt  => new Date(dt).toLocaleDateString('en-CA', { dateStyle: 'medium' });
  const fmtDateTime = dt  => dt ? new Date(dt).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : null;

  // Cash/e-transfer (payment_type_id 1) is settled via paid_dtm; donations
  // (payment_type_id 2) are settled via pos_dtm. Any other payment type has
  // no settlement date yet.
  const getPaidAt = t => {
    if (t.payment_type_id === 1) return t.paid_dtm;
    if (t.payment_type_id === 2) return t.pos_dtm;
    return null;
  };

  const fetchLines = async transactionId => {
    setLinesCache(prev => ({ ...prev, [transactionId]: { status: 'loading', lines: [] } }));
    try {
      const res = await axios.get(`/api/transactions/${transactionId}/lines`);
      setLinesCache(prev => ({ ...prev, [transactionId]: { status: 'ready', lines: res.data.lines } }));
    } catch {
      setLinesCache(prev => ({ ...prev, [transactionId]: { status: 'error', lines: [] } }));
    }
  };

  const toggleRow = transactionId => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(transactionId)) {
        next.delete(transactionId);
      } else {
        next.add(transactionId);
        if (!linesCache[transactionId]) fetchLines(transactionId);
      }
      return next;
    });
  };

  if (loading) return <div className="history-page"><p>Loading…</p></div>;
  if (error)   return <div className="history-page"><p className="history-error">{error}</p></div>;

  return (
    <div className="history-page">
      <h2>TRANSACTION HISTORY</h2>

      {totals && (
        <div className="totals-bar">
          <div className="total-card">
            <span className="total-label">Total Earned</span>
            <span className="total-value">{fmt(totals.total_earned)}</span>
          </div>
          <div className="total-card">
            <span className="total-label">Paid Out</span>
            <span className="total-value">{fmt(Number(totals.total_paid) + Number(totals.total_donated))}</span>
          </div>
          <div className="total-card total-card--pending">
            <span className="total-label">Pending</span>
            <span className="total-value">{fmt(totals.total_pending)}</span>
          </div>
        </div>
      )}

      {transactions.length === 0 ? (
        <p className="no-transactions">No transactions yet.</p>
      ) : (
        <div className="history-table-scroll">
          <table className="history-main-table">
            <thead>
              <tr>
                <th>Label</th>
                <th className="col-date">Date</th>
                <th className="col-site">Site</th>
                <th>Amount</th>
                <th className="col-status">Status</th>
                <th className="col-toggle"></th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => {
                const isDonated = t.payment_type_id === 2 && t.pos_dtm != null;
                const status    = t.paid_dtm ? 'paid' : isDonated ? 'donated' : 'pending';
                const title     = t.paid_dtm    ? `Paid ${fmtDate(t.paid_dtm)}`
                                : isDonated     ? `Donated ${fmtDate(t.pos_dtm)}`
                                : undefined;
                const isExpanded = expandedIds.has(t.transaction_id);
                const cache      = linesCache[t.transaction_id];

                return (
                  <React.Fragment key={t.transaction_id}>
                    <tr className="tx-row" onClick={() => toggleRow(t.transaction_id)}>
                      <td>{t.label_id}</td>
                      <td className="col-date">{fmtDate(t.processed_dtm)}</td>
                      <td className="col-site">{t.site}</td>
                      <td>{fmt(t.total_amt)}</td>
                      <td className="col-status"><StatusBadge status={status} title={title} bonus={t.bonus_applied} /></td>
                      <td className="col-toggle">
                        <button
                          className="tx-toggle-btn"
                          aria-label={isExpanded ? 'Hide breakdown' : 'Show breakdown'}
                          aria-expanded={isExpanded}
                          onClick={e => { e.stopPropagation(); toggleRow(t.transaction_id); }}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="tx-lines-row">
                        <td colSpan={6}>
                          {(!cache || cache.status === 'loading') && (
                            <p className="tx-lines-status">Loading breakdown…</p>
                          )}
                          {cache?.status === 'error' && (
                            <p className="tx-lines-status tx-lines-status--error">Failed to load breakdown.</p>
                          )}
                          {cache?.status === 'ready' && (
                            <TransactionLines
                              lines={cache.lines}
                              fmt={fmt}
                              fmtDateTime={fmtDateTime}
                              totalAmt={t.total_amt}
                              transaction={t}
                              paidAt={getPaidAt(t)}
                            />
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TransactionLines({ lines, fmt, fmtDateTime, totalAmt, transaction, paidAt }) {
  const subtotal = lines.reduce((sum, line) => {
    const qty = line.payment_qtty ?? line.qtty;
    return sum + qty * Number(line.price);
  }, 0);
  const total = Number(totalAmt);
  const bonus = total - subtotal;
  const hasBonus = bonus > 0.001;

  return (
    <div className="tx-lines">
      <div className="tx-info-grid">
        <div className="tx-info-col">
          <h4 className="tx-info-heading">Processing Information</h4>
          <div className="tx-info-row">
            <span className="tx-info-label">Site</span>
            <span className="tx-info-value">{transaction.site}</span>
          </div>
          <div className="tx-info-row">
            <span className="tx-info-label">Processed On</span>
            <span className="tx-info-value">{fmtDateTime(transaction.processed_dtm)}</span>
          </div>
        </div>
        <div className="tx-info-col">
          <h4 className="tx-info-heading">Payment Information</h4>
          {paidAt && (
            <div className="tx-info-row">
              <span className="tx-info-label">Paid</span>
              <span className="tx-info-value">{fmtDateTime(paidAt)}</span>
            </div>
          )}
          <div className="tx-info-row">
            <span className="tx-info-label">Payment Method</span>
            <span className="tx-info-value">{transaction.payment_type_description}</span>
          </div>
        </div>
      </div>

      {lines.length === 0 ? (
        <p className="tx-lines-status">No line items found.</p>
      ) : (
        <>
          <div className="tx-lines-grid">
            <div className="tx-lines-grid-row tx-lines-grid-row--head">
              <span>Container</span>
              <span>Qty</span>
              <span>Value</span>
            </div>
            {lines.map(line => {
              const qty   = line.payment_qtty ?? line.qtty;
              const value = qty * Number(line.price);

              return (
                <div className="tx-lines-grid-row" key={line.line_no}>
                  <span data-label="Container">{line.description}</span>
                  <span data-label="Qty">{qty}</span>
                  <span data-label="Value">{fmt(value)}</span>
                </div>
              );
            })}
          </div>

          <div className="tx-summary">
            <div className="tx-summary-line">
              <span>SUBTOTAL</span>
              <span>{fmt(subtotal)}</span>
            </div>
            {hasBonus && (
              <div className="tx-summary-line tx-summary-line--bonus">
                <span>BONUS</span>
                <span>+{fmt(bonus)}</span>
              </div>
            )}
            <div className="tx-summary-line tx-summary-line--total">
              <span>TOTAL</span>
              <span>{fmt(total)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default History;
